/**
 * Trip board API calls: the trip-owned board and board references
 * (trips/albums pinned to it).
 *
 * Boards LINKED to a trip (a user's own travel board attached to a trip) were
 * retired when trip-owned boards replaced them; their wrappers and endpoints
 * are gone. Nothing called them.
 */
import { apiRequest } from './request';

/**
 * Get trip's owned board with collections and references
 * @param {string} tripId - Trip ID
 * @param {boolean} createIfMissing - Create board if it doesn't exist
 * @returns {object} Result with board, collections, references
 */
export function getTripOwnedBoard(tripId, createIfMissing = false) {
  const path = createIfMissing
    ? `/api/trips/${tripId}/owned-board?create=true`
    : `/api/trips/${tripId}/owned-board`;
  return apiRequest(path, {
    errorLabel: 'getting trip owned board'
  });
}

/**
 * Where each of the viewer's saved lists has its places (positions only), for
 * the Map tab's Saved chips. See server/services/savedListPositions.js.
 * @param {string} tripId - Trip ID
 * @returns {object} Result with lists: [{ kind, id, points: [[lat, lng], ...] }]
 */
export function getSavedListPositions(tripId) {
  return apiRequest(`/api/trips/${tripId}/saved-positions`, {
    errorLabel: 'getting saved list positions'
  });
}

/**
 * Create trip's owned board
 * @param {string} tripId - Trip ID
 * @param {string} name - Optional custom board name
 * @returns {object} Result with board
 */
export function createTripOwnedBoard(tripId, name = null) {
  return apiRequest(`/api/trips/${tripId}/owned-board`, {
    method: 'POST',
    body: { name },
    errorLabel: 'creating trip owned board'
  });
}

/**
 * Add a reference (trip/album) to the board
 * @param {string} tripId - Trip ID
 * @param {object} data - { referenceType, referenceId, notes? }
 * @returns {object} Result with reference
 */
export function addTripBoardReference(tripId, data) {
  return apiRequest(`/api/trips/${tripId}/references`, {
    method: 'POST',
    body: data,
    errorLabel: 'adding board reference'
  });
}

/**
 * Remove a reference from the board
 * @param {string} tripId - Trip ID
 * @param {number} referenceId - Reference ID
 * @returns {object} Result
 */
export function removeTripBoardReference(tripId, referenceId) {
  return apiRequest(`/api/trips/${tripId}/references/${referenceId}`, {
    method: 'DELETE',
    errorLabel: 'removing board reference'
  });
}

/**
 * The Ideas tab's one read: the board's lists, every list's items (each with
 * its collection_id), references, and each idea's votes and notes keyed
 * `${content_type}:${content_id}` (server/services/tripIdeasService.js).
 * @param {string} tripId - Trip ID
 */
export function getTripIdeas(tripId) {
  return apiRequest(`/api/trips/${tripId}/ideas`, {
    errorLabel: 'getting trip ideas'
  });
}

/**
 * "I'm in" on or off for an idea on the trip.
 * @returns {object} Result with voters: the idea's voters after the change
 */
export function setTripIdeaVote(tripId, contentType, contentId, isIn, strength = 1) {
  return apiRequest(`/api/trips/${tripId}/ideas/vote`, {
    method: 'PUT',
    // strength 2 = "Can't miss" (the star); anything else is a plain "I'm in"
    body: { content_type: contentType, content_id: contentId, in: !!isIn, strength: strength === 2 ? 2 : 1 },
    errorLabel: 'saving idea vote'
  });
}

/**
 * A note on an idea, for everyone on the trip.
 * @returns {object} Result with note
 */
export function addTripIdeaNote(tripId, contentType, contentId, body) {
  return apiRequest(`/api/trips/${tripId}/ideas/notes`, {
    method: 'POST',
    body: { content_type: contentType, content_id: contentId, body },
    errorLabel: 'adding idea note'
  });
}

/** Delete your own note on an idea. */
export function deleteTripIdeaNote(tripId, noteId) {
  return apiRequest(`/api/trips/${tripId}/ideas/notes/${noteId}`, {
    method: 'DELETE',
    errorLabel: 'deleting idea note'
  });
}
